import { type UserType } from '../user/userType';

export enum INTEGRATION_PROVIDER_ID {
  ADROLL_ADS = 'oprv:adroll',
  MICROSOFT_ADS = 'oprv:bing',
  DEMANDBASE_ADS = 'oprv:demandbase',
  FACEBOOK_ADS = 'oprv:facebook',
  G2 = 'oprv:g2',
  GOOGLE_AD_MANAGER = 'oprv:googleadmanager',
  GOOGLE_ADS = 'oprv:google',
  HUBSPOT = 'oprv:hubspot',
  LINKEDIN = 'oprv:linkedin',
  REDDIT_ADS = 'oprv:reddit',
  ROLLWORKS_ADS = 'oprv:rollworks',
  SALESFORCE = 'oprv:salesforce',
  SLACK = 'oprv:slack',
  STACK_ADAPT = 'oprv:stackadapt',
  TIKTOK_ADS = 'oprv:tiktok',
  X_ADS = 'oprv:twitter',
}

export type IntegrationProviderCategory = 'crm' | 'advertising' | 'ai';

export type IntegrationProviderCapability = 'audienceActivation' | 'intent';
export type IntegrationSettingName = 'audienceType' | 'minSize';

type BaseIntegrationSetting = {
  name: IntegrationSettingName;
};

export type AudienceMinSizeIntegrationSetting = BaseIntegrationSetting & {
  name: 'minSize';
  stringValue: string; // number wrapped in a string
};

export type AudienceTypeIntegrationSetting = BaseIntegrationSetting & {
  name: 'audienceType';
  stringValue: 'email' | 'domain' | 'ip';
};

export type IntegrationSetting = AudienceTypeIntegrationSetting | AudienceMinSizeIntegrationSetting;

export type IntegrationProviderCapabilityType = {
  id: IntegrationProviderCapability;
  settings: IntegrationSetting[];
};

export type IntegrationProviderType = {
  id: INTEGRATION_PROVIDER_ID;
  name: string;
  startUrl: string;
  type: string;
  multiConnection: boolean;
  syncIntervalHours: number;
  category: IntegrationProviderCategory;
  capabilities: IntegrationProviderCapabilityType[];
  available: boolean;
  connections: IntegrationConnectionType[];
};

export type IntegrationJobType = {
  created_at: string;
  status?: 'error' | 'completed' | 'processing';
  errors?: string | null;
} | null;

type IntegrationConnectionHealthCheckBase = {
  name: string;
  description: string;
};

export type HealthCheckFieldAccessErrorType = IntegrationConnectionHealthCheckBase & {
  type: 'field-access-error';
  details: {
    source: {
      object: string;
      fields: string[];
    }[];
  };
};

export type HealthCheckAPIAccessErrorType = IntegrationConnectionHealthCheckBase & {
  type: 'api-access-error';
};

export type IntegrationConnectionHealthCheckType =
  | HealthCheckFieldAccessErrorType
  | HealthCheckAPIAccessErrorType
  | (IntegrationConnectionHealthCheckBase & {
      type: string;
      details?: Record<string, unknown>;
    });

export type IntegrationConnectionDetailGQLResponse = {
  oAuthConnections: {
    getById: {
      healthChecks: IntegrationConnectionHealthCheckType[];
    };
  };
};

export type IntegrationConnectionType = {
  id: string;
  createdBy?: UserType;
  lastJob?: IntegrationJobType;
  organizationId?: string;
  organizationName?: string;
  status: 'active' | 'inactive';
};

export type IntegrationOrgType = {
  id: string;
  name: string;
};

export type IntegrationProviderListGQLResponse = {
  oAuthProviders: {
    get: {
      edges: {
        node: Omit<IntegrationProviderType, 'connections'> & {
          connections: {
            edges: {
              node: IntegrationConnectionType;
            }[];
          };
        };
      }[];
    };
  };
};

export type IntegrationTokenCreateVariablesType = {
  oAuthProviderId: string;
  state: string;
  autoCreate: boolean;
  params: {
    name: string;
    value: string;
  }[];
};

export type IntegrationTokenCreateGQLResponse = {
  oAuthTokens: {
    createWithState: {
      id: string;
      orgs?: IntegrationOrgType[];
      connection?: {
        id: string;
      };
    };
  };
};

export type IntegrationConnectionCreateVariablesType = {
  oAuthTokenId: string;
  organizationId: string;
  state: string;
};

export type IntegrationConnectionCreateGQLResponse = {
  oAuthConnections: { createWithState: { id: string } };
};

export type IntegrationConnectionDeleteVariablesType = {
  id: string;
};

export type IntegrationConnectionDeleteGQLResponse = {
  oAuthConnections: {
    delete: {
      success: boolean;
      id: string;
    };
  };
};

export type IntegrationConnectionAPIKeyCreateVariablesType = {
  providerId: string;
  settings: string;
};

export type IntegrationConnectionAPIKeyCreateGQLResponse = {
  oAuthConnections: {
    createForProvider: {
      id: string;
    };
  };
};
